/**
 * Whether the signed-in person can open a page by its path in the current
 * office scope: the route exists, its permission is held there and its
 * module's entitlement is readable. Family tabs (FamilyTabs.tsx) and the
 * schedule's "Other views" menu show only what this allows.
 */
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useOfficeScope } from '../office/OfficeScope.js';
import type { ShellRouteData } from '../router.js';
import { canInScope, entitlementFor } from '../session/access.js';
import { useSession } from '../session/session.js';

type RouteRecord = { options?: { staticData?: { shell?: ShellRouteData } } } | undefined;

export function useCanOpenPath(): (path: string) => boolean {
  const router = useRouter();
  const session = useSession();
  const office = useOfficeScope();
  const routes = router.routesByPath as Record<string, RouteRecord>;
  return useCallback(
    (path: string) => {
      const route = routes[path];
      if (!route) return false;
      const shell = route.options?.staticData?.shell;
      const perm = shell?.route.permission;
      if (perm && !canInScope(session, perm, office.locationIds)) return false;
      return entitlementFor(session, shell?.entitlementKey ?? null, office.locationIds).readable;
    },
    [routes, session, office.locationIds],
  );
}
